import type { DensitySnapshot, InstrumentRef, PinView, SideView } from "@screener/protocol";
import { formatPrice } from "./format";

/**
 * Сопоставление строки таблицы §9 с закреплением §6.7 (1.18). Признака
 * закрепления в снимке плотности нет — клиент находит его сам (design.md
 * решение 1a, web-density-table).
 *
 * Строка закреплена только по точному совпадению уровня — биржа, рынок,
 * монета, котировка, сторона и нормализованная цена. Точного совпадения
 * недостаточно для сценария «якорь сдвинулся»: якорь мигрирует внутри
 * живого кластера событием `Moved` (§4.3), и цена строки уходит от
 * закреплённой ровно тогда, когда подсветка нужна. Поэтому вкладка
 * дополнительно помнит `density_id` плотности, с которой закрепление было
 * поставлено, — пока эта плотность жива, её строка остаётся отмеченной,
 * пока новое точное совпадение не перетянет отметку на себя. Сопоставление
 * по близости или по единственности кандидата — намеренно не делается:
 * это назвало бы расхождение в процентах от цены, которой человек не
 * закреплял (design.md, отвергнутый вариант «единственный кандидат»).
 */

/**
 * Приводит десятичную запись цены к единому виду: без ведущего знака `+`,
 * без лишних ведущих нулей целой части, без хвостовых нулей и точки в конце
 * дробной. `"0.010"`, `"+00.01"` и `".01"` — одно и то же число с разным
 * форматированием, а масштаб приходит от биржи и меняется вместе с шагом
 * цены (design.md решение 1). Само число не пересчитывается — сравнивается
 * только его текстовый вид без форматирующего шума.
 *
 * Экспоненциальная запись (`"1e2"`) не приводится к общему виду: обе стороны
 * сравнения приходят из `rust_decimal`, который её не отдаёт.
 */
export function normalizePrice(value: string): string {
  let trimmed = value.trim();
  if (trimmed.startsWith("+")) {
    trimmed = trimmed.slice(1);
  }
  const negative = trimmed.startsWith("-");
  if (negative) {
    trimmed = trimmed.slice(1);
  }

  const [rawInteger, rawFraction] = trimmed.split(".");
  let integerPart = rawInteger === undefined || rawInteger === "" ? "0" : rawInteger;
  // Ведущие нули целой части: `00.5` и `0.5` — один и тот же уровень.
  integerPart = integerPart.replace(/^0+(?=\d)/, "");

  let normalized = integerPart;
  if (rawFraction !== undefined) {
    const withoutTrailingZeros = rawFraction.replace(/0+$/, "");
    if (withoutTrailingZeros !== "") {
      normalized += `.${withoutTrailingZeros}`;
    }
  }

  return negative && normalized !== "0" ? `-${normalized}` : normalized;
}

function levelKey(instrument: InstrumentRef, side: SideView, price: string): string {
  return `${instrument.exchange}|${instrument.market}|${instrument.base}|${instrument.quote}|${side}|${normalizePrice(price)}`;
}

/** Закрепление, относящееся к уровню строки, вместе с тем, выровнена ли строка. */
export interface RowPinMatch {
  pin: PinView;
  /** Якорь строки равен закреплённой цене: подсветки расхождения быть не должно. */
  aligned: boolean;
}

/**
 * Память вкладки: на какой плотности стояло закрепление в момент, когда его
 * поставили (`use-pins.ts`). Ключ — идентификатор закрепления, значение —
 * `density_id`. Эфемерна, перезагрузку вкладки не переживает (design.md
 * решение 1a) — вызывающая сторона не обязана её нигде сохранять.
 */
export type PinMemory = Map<string, string>;

/**
 * Сопоставляет список закреплений со списком живых строк. Строка становится
 * закреплённой одним из двух способов: её уровень (биржа, рынок, монета,
 * котировка, сторона и цена) точно совпал с закреплением, либо это та самая
 * плотность, с которой закрепление было поставлено в этой вкладке (память
 * `memory`) — тогда строка считается закреплённой, но разошедшейся (сценарий
 * «Якорь сдвинулся», web-density-table). Точное совпадение всегда сильнее
 * памяти — по всем закреплениям сразу, а не только в пределах одного: два
 * прохода, сначала все точные совпадения, затем память только по строкам,
 * которые точным совпадением не заняты (design.md решение 1a). Ни близость,
 * ни единственность кандидата основанием не служат: несколько строк одного
 * уровня без точного совпадения и без памяти не сопоставляются вовсе.
 */
export function matchPinsToRows(
  pins: PinView[],
  rows: DensitySnapshot[],
  memory: PinMemory,
): Map<string, RowPinMatch> {
  const result = new Map<string, RowPinMatch>();
  const rowsById = new Map(rows.map((row) => [row.density_id, row]));

  // Ключи строк собираются один раз за вызов, а не на каждую пару
  // закрепление×строка: сопоставление перестраивается на каждое событие
  // потока, и O(закрепления × строки) со склейкой ключа на каждую пару здесь
  // не годится.
  const rowsByKey = new Map<string, DensitySnapshot>();
  for (const row of rows) {
    const key = levelKey(row.instrument, row.side, row.anchor_price);
    // Уровень строки не бывает у двух живых плотностей сразу (§4): при
    // случайном совпадении ключа предпочитается первая — детерминированно,
    // не по случаю обхода массива.
    if (!rowsByKey.has(key)) {
      rowsByKey.set(key, row);
    }
  }

  // Первый проход: точные совпадения по всем закреплениям сразу.
  const exactRowIdByPinId = new Map<string, string>();
  for (const pin of pins) {
    const key = levelKey(pin.instrument, pin.side, pin.price);
    const exact = rowsByKey.get(key);
    if (exact !== undefined) {
      result.set(exact.density_id, { pin, aligned: true });
      exactRowIdByPinId.set(pin.id, exact.density_id);
    }
  }

  // Второй проход: память — только по закреплениям без своего точного
  // совпадения и только по строкам, ещё не занятым чужим точным совпадением.
  for (const pin of pins) {
    if (exactRowIdByPinId.has(pin.id)) {
      continue;
    }
    const rememberedId = memory.get(pin.id);
    if (rememberedId === undefined || result.has(rememberedId)) {
      continue;
    }
    const remembered = rowsById.get(rememberedId);
    if (remembered !== undefined) {
      result.set(remembered.density_id, { pin, aligned: false });
    }
  }

  return result;
}

/**
 * Величина и направление расхождения якоря с закреплённой ценой (§9, §31):
 * знак сам называет направление, а не цвет в одиночку. Расчёт — тот, что
 * design.md прямо отдаёт клиенту («из двух чисел — текущего якоря и
 * закреплённой цены»), а не пересчёт домена.
 *
 * `null` — величину посчитать нельзя вовсе (нечисловая цена или закреплённая
 * цена — ноль): вызывающая сторона обязана не подсвечивать строку и не
 * показывать величину, а не печатать пустоту, которая выглядит как «нет
 * расхождения» наравне с настоящим нулём.
 */
export function formatPriceDrift(anchorPrice: string, pinPrice: string): string | null {
  const anchor = Number(anchorPrice);
  const pinned = Number(pinPrice);
  if (!Number.isFinite(anchor) || !Number.isFinite(pinned) || pinned === 0) {
    return null;
  }
  const deltaPct = ((anchor - pinned) / pinned) * 100;
  if (deltaPct === 0) {
    return "0.00%";
  }
  // Расхождение меньше 0.01% печаталось бы как `+0.00%` — одновременно «якорь
  // ушёл» подсветкой и «никуда не ушёл» числом. Величина здесь ненулевая, но
  // называть её нечем точнее «меньше сотой процента».
  if (Math.abs(deltaPct) < 0.01) {
    return "<0.01%";
  }
  const sign = deltaPct > 0 ? "+" : "";
  return `${sign}${deltaPct.toFixed(2)}%`;
}

/**
 * Подпись кнопки закрепления — общая для строки таблицы и действий детальной
 * панели (design.md change stage6-density-cards, решение 8): обе кнопки
 * закрепляют один и тот же уровень одним и тем же запросом, и подпись обязана
 * называть его одинаково, а не разойтись копией текста.
 */
export function pinButtonLabel(pinMatch: RowPinMatch | "unknown" | undefined): string {
  if (pinMatch === "unknown") {
    return "Закрепление уровня: неизвестно — список не прочитан";
  }
  if (pinMatch !== undefined) {
    return `Снять закрепление уровня — закреплено на ${formatPrice(pinMatch.pin.price)}`;
  }
  return "Закрепить уровень";
}

/** Состояние закрепления строки, выведенное из сопоставления §6.7 — общее у строки таблицы и строки карточки. */
export interface RowPinState {
  /** Сопоставление без служебных значений «неизвестно» — `null`, если строка не закреплена. */
  match: RowPinMatch | null;
  /** Строка несёт значок закрепления. */
  pinned: boolean;
  /** Величина и направление расхождения якоря с закреплённой ценой, если есть. */
  driftText: string | null;
  /** Строка обязана нести подсветку расхождения. */
  diverged: boolean;
}

/**
 * Выводит состояние закрепления строки из сопоставления `pinMatch` (design.md
 * change stage6-density-cards, решение — общая логика строки таблицы и
 * строки карточки, review блок C): обе строки читают одно и то же
 * сопоставление одинаково, а не переписывают вывод по отдельности.
 */
export function rowPinState(
  row: DensitySnapshot,
  pinMatch: RowPinMatch | "unknown" | undefined,
): RowPinState {
  const match = pinMatch === "unknown" || pinMatch === undefined ? null : pinMatch;
  // Строка «закреплённая», когда якорь равен закреплённой цене; расхождение
  // (событие `Moved` §4.3 внутри того же кластера) не снимает значок — только
  // подсветку (web-density-table «Якорь сдвинулся»).
  const pinned = match !== null;
  // Величину расхождения может быть нечем назвать (нечисловая цена или
  // закреплённая цена — ноль): тогда подсветки быть не должно, иначе цвет
  // остался бы единственным носителем смысла (review stage5-notes-and-pin, п. 8).
  const driftText =
    match !== null && !match.aligned ? formatPriceDrift(row.anchor_price, match.pin.price) : null;
  const diverged = driftText !== null;
  return { match, pinned, driftText, diverged };
}
